
import React from 'react';
import { ComboBox, ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import {Input, InputChangeEvent} from '@progress/kendo-react-inputs';
import styles from './_lease-editor.module.scss';
import {Error} from "@progress/kendo-react-labels";
import {useSelector} from "react-redux";
import {hasValue, toDollars, DatePickerWithYearSanitizer} from "@amsanalytics/react-shared-library";
import {Alert} from "@mui/material";
import {ILeaseLoanClass} from "../../../../../Interfaces/Common/ILeaseLoanClass";
import {IApplication} from "../../../../../Interfaces/Application/IApplication";
import {IRuleViolation} from "../../../../../Interfaces/Application/IApplicationQuote";
import {ILookupItems} from "../../../../../Interfaces/ILookupItems";
import {AppState} from "../../../../../store/rootReducer";
import {
  getLeaseQuoteErrorMessages
} from "../../../../../utils/quoteUtils";
import {
  interestRateErrorMessage,
  isInterestRateValid,
  isNumberOfPaymentsValid, getNumberOfPaymentsErrorMessage
} from "../../../../../utils/LoanCalcUtils";
import QuoterError from "../../../QuoterError/QuoterError";
import {getEmployeeName} from "../../../../../utils/utils";
import {getLeaseQuoteTotalOfPaymentsCalc} from "../../../../../utils/appUtils";
import {IQuotePayType} from "../../../../../Interfaces/Common/IQuotePayType";

export interface LeasingEditorProps {
  selectedLeaseLoanClass: ILeaseLoanClass | undefined;
  onChangeSingleField: (fieldName: string, value: any) => void;
  currentApp: IApplication;
  applicationLeaseErrors: IRuleViolation[];
  lookups: ILookupItems;
  leaseQuoteFieldIsDisabled: (fieldName: string) => boolean;
  selectedPaymentFrequency: IQuotePayType | undefined;
}

export default function LeasingEditor(props: LeasingEditorProps) {
  const applicationLeaseQuote = props.currentApp.applicationLeaseQuote;
  const employees = useSelector((state: AppState) => state.applications.employees);
  const quotePayTypes = useSelector((state: AppState) => state.applications.quotePayTypes);

  const interestRateIsValid = isInterestRateValid(applicationLeaseQuote.apr, props.selectedLeaseLoanClass?.minApr, props.selectedLeaseLoanClass?.maxApr);
  const interestRateMessage = interestRateErrorMessage(props.selectedLeaseLoanClass?.minApr, props.selectedLeaseLoanClass?.maxApr);
  
  const numberOfPaymentsIsValid = isNumberOfPaymentsValid(applicationLeaseQuote.numberOfPayments, applicationLeaseQuote.contractType, applicationLeaseQuote.solveFor);
  const numberOfPaymentsMessage = getNumberOfPaymentsErrorMessage(applicationLeaseQuote.numberOfPayments, applicationLeaseQuote.contractType, applicationLeaseQuote.solveFor);

  const errorMessages= getLeaseQuoteErrorMessages(props.applicationLeaseErrors);

  const totalOfPayments = getLeaseQuoteTotalOfPaymentsCalc(applicationLeaseQuote);
  
  return (
    <div className={styles.leasingEditorContainer}>
      <div className={styles.sectionItem}>
        <DatePickerWithYearSanitizer
          disabled={props.leaseQuoteFieldIsDisabled("firstPmtDate")}
          valid={!hasValue(errorMessages.firstPmtDate)}
          label={'First Payment Date'}
          value={hasValue(applicationLeaseQuote.firstPmtDate) ? new Date(applicationLeaseQuote.firstPmtDate!) : null}
          onChange={(event: DatePickerChangeEvent) => props.onChangeSingleField('firstPmtDate', event.value)}
        />
        <QuoterError message={errorMessages.firstPmtDate}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly={props.leaseQuoteFieldIsDisabled("termInMonths")}
          valid={!hasValue(errorMessages.termInMonths)}
          label={'Term in Months'}
          value={applicationLeaseQuote.termInMonths}
          type='number'
          min={1}
          onChange={(e: InputChangeEvent) => props.onChangeSingleField('termInMonths', Number(e.target.value))}
        />
        <QuoterError message={errorMessages.termInMonths}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly={props.leaseQuoteFieldIsDisabled("numberOfPmts")}
          valid={numberOfPaymentsIsValid && !hasValue(errorMessages.numberOfPmts)}
          label={'Number of Payments'}
          value={applicationLeaseQuote.numberOfPmts}
          type='number'
          min={1}
          max={props.selectedLeaseLoanClass?.maxNoPayments}
          onChange={(e: InputChangeEvent) => props.onChangeSingleField('numberOfPmts', Number(e.target.value))}
        />
        {!numberOfPaymentsIsValid && <Error>{numberOfPaymentsMessage}</Error>}
        <QuoterError message={errorMessages.numberOfPmts}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly={props.leaseQuoteFieldIsDisabled("apr")}
          valid={interestRateIsValid && !hasValue(errorMessages.apr)}
          label={'Rate'}
          value={applicationLeaseQuote?.apr ?? 0}
          type='number'
          max={props.selectedLeaseLoanClass?.maxApr}
          onChange={(e: InputChangeEvent) => props.onChangeSingleField('apr', Number(e.target.value))}
        />
        {!interestRateIsValid && <Error>{interestRateMessage}</Error>}
        <QuoterError message={errorMessages.apr}/>
      </div>
      <div className={styles.sectionItem}>
        <ComboBox
          disabled={props.leaseQuoteFieldIsDisabled("paymentFrequencyId")}
          valid={!hasValue(errorMessages.paymentFrequencyId)}
          label={'Pay Frequency'}
          data={quotePayTypes}
          textField="name"
          suggest={true}
          clearButton={false}
          value={props.selectedPaymentFrequency}
          onChange={(event: ComboBoxChangeEvent) => props.onChangeSingleField('paymentFrequencyId', event.value.id as string)}
        />
        <QuoterError message={errorMessages.paymentFrequencyId}/>
      </div>
      {hasValue(applicationLeaseQuote.calcErrorDescription) &&
        <div className={styles.sectionItem}>
          <Alert severity="error">{applicationLeaseQuote.calcErrorDescription}</Alert>
        </div>
      }
      {hasValue(applicationLeaseQuote.fullPaymentAmount) &&
        <div>
          <div className={styles.sectionItem}>
            <Input
              readOnly
              valid={!hasValue(errorMessages.fullPaymentAmount)}
              label={(applicationLeaseQuote.solutionNumberOfPmts ?? 0) > 0 ? `${applicationLeaseQuote.solutionNumberOfPmts} Payments of` : 'Payment'}
              value={applicationLeaseQuote.fullPaymentAmount}
              type='number'
            />
            <QuoterError message={errorMessages.fullPaymentAmount}/>
          </div>
          <div className={styles.sectionItem}>
            <Input
              readOnly
              valid={!hasValue(errorMessages.moneyFactor)}
              label={'Money Factor'}
              value={applicationLeaseQuote.moneyFactor?.toFixed(5)}
              type='text'
            />
            <QuoterError message={errorMessages.moneyFactor}/>
          </div>
          <div className={styles.sectionItem}>
            <Input
              readOnly
              valid={!hasValue(errorMessages.totalOfPayments)}
              label={'Total of Payments'}
              value={toDollars(totalOfPayments)}
              type='text'
            />
            <QuoterError message={errorMessages.totalOfPayments}/>
          </div>
          <div className={styles.sectionItem}>
            <Input
              readOnly
              valid={!hasValue(errorMessages.capCost)}
              label={'Cap Cost'}
              value={toDollars(applicationLeaseQuote.capCost)}
              type='text'
            />
            <QuoterError message={errorMessages.capCost}/>
          </div>
          <div className={styles.sectionItem}>
            <Input
              readOnly
              valid={!hasValue(errorMessages.totalDepreciation)}
              label={'Total Depreciation'}
              value={toDollars(applicationLeaseQuote.totalDepreciation)}
              type='text'
            />
            <QuoterError message={errorMessages.totalDepreciation}/>
          </div>
          <div className={styles.sectionItem}>
            <Input
              readOnly
              valid={!hasValue(errorMessages.residualValue)}
              label={'Residual Value'}
              value={toDollars(applicationLeaseQuote.residualValue)}
              type='text'
            />
            <QuoterError message={errorMessages.residualValue}/>
          </div>
          <div className={styles.sectionItem}>
            <Input
              readOnly
              valid={!hasValue(errorMessages.totalDueAtSigningCash)}
              label={'Total Down'}
              value={toDollars(applicationLeaseQuote.totalDueAtSigningCash)}
              type='text'
            />
            <QuoterError message={errorMessages.totalDueAtSigningCash}/>
          </div>
          <div className={styles.sectionItem}>
            <Input
              readOnly
              valid={!hasValue(errorMessages.taxPortionOfDownPayment)}
              label={'DP Tax'}
              value={toDollars(applicationLeaseQuote.taxPortionOfDownPayment)}
              type='text'
            />
            <QuoterError message={errorMessages.taxPortionOfDownPayment}/>
          </div>
          {applicationLeaseQuote.quoteGeneratedBy && applicationLeaseQuote.quoteGeneratedDate &&
            <div className={styles.sectionItem}>
              <Input
                readOnly
                label={'Quote Generated By'}
                value={`${getEmployeeName(employees, applicationLeaseQuote.quoteGeneratedBy)} at ${new Date(applicationLeaseQuote.quoteGeneratedDate).toLocaleString()}`}
                type='text'
              />
            </div>
          }
        </div>
      }
    </div>
  );
}
